import React from "react";
import { Button } from "../../../";
import { ReportStatusSelectors } from "../constants";
import { Controller, useFormContext } from "react-hook-form";
import { openConfirmModal } from "../../ConfirmModal";
import { useReportControls } from "../hooks/ReportControlContext";
import { useReportMessage } from "../hooks/ReportMessageContext";
import { useReportSendOptions } from "../hooks/ReportPublishContext";
import { DateInput, FormInput, FormSelect, NumberInput } from "../../../forms";
import { GenericUserSelect, ReportAuthorSelect, ReportTypesSelect } from "../../../forms/selects";
import { ClientReportStatusRecipients } from "../types";
import { DefReactSelectOption } from "../../../../types/DefReactSelectOption";
import { PatchClientReportForm } from "../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faUserAltSlash } from "@fortawesome/free-solid-svg-icons";

export type FromBodyProps = {
    isLoading: boolean
    isSendOptionsAvailable?: boolean
};

/**
 * This is the main body of the edit report modal that contains the majority of the editable fields
 */
export const FormBody = ({ isLoading: isProcessing, isSendOptionsAvailable = false } : Readonly<FromBodyProps>) => {
    const statusControlRef = React.useRef(null);
    const { control, formState, setValue, watch } = useFormContext<PatchClientReportForm>();
    const authorId = watch('authorId');
    const status = watch('status');
    const statusId = watch('statusId');

    const { openSendOptions } = useReportSendOptions();
    const { clearMessage, createMessage } = useReportMessage();
    const { 
        currentStatus,
        currentUserId,
        isAllowedToAssignAuthor,
        isAllowedToAssignAuthorQueue,
        isAllowedToAssignCompliance,
        isAllowedToSetActualMinutes, 
        isAllowedToSetDeadlineDate,
        isAllowedToSetReportType, 
        isLoading: isLoadingStatuses,
        isReportEditable,
        report,
        reportStatuses,
        reportThreadId
    } = useReportControls();

    const onAuthorSelected = (values: any) => {
        setValue('authorId', values.value, { shouldDirty: true });

        let newStatus : ClientReportStatusRecipients | null;
        if (!currentStatus?.isTerminalStatus && !currentStatus?.isComplianceStatus) {
            // Set to Author Assigned if the status is neither terminal nor compliance
            newStatus = reportStatuses.find(el => el.data!.selector === ReportStatusSelectors.AuthorAssigned)?.data ?? null;
            if (newStatus) {
                setValue('statusId', newStatus.id, { shouldDirty: true });
                setValue('status', newStatus.selector, { shouldDirty: true });
            }
        } else {
            newStatus = currentStatus;
        }

        createMessage(reportThreadId!, newStatus!, values.value);
    }

    const onOpenSendOptions = () => openSendOptions();

    const onReleaseReport = () => {
        openConfirmModal({
            size: 'sm',
            cancelButtonText: "Cancel",
            confirmButtonText: "Yes, Release Report",
            message: "Are you sure you want to release this report back into the await author queue? This will allow another user to assign the report to either themselves or another user.",
        }).then((r) => {
            if (!r) {
                return;
            }

            // remove the author-id value
            setValue('authorId', null, { shouldDirty: true });

            // we also want to grab and set the status of the report back to "Await Author" as it returns to the "pool"
            const awaitAuthorStatus = reportStatuses.find(el => el.data!.selector === ReportStatusSelectors.AwaitAuthor)?.data ?? null;
            if (awaitAuthorStatus) {
                setValue('statusId', awaitAuthorStatus.id, { shouldDirty: true });
                setValue('status', awaitAuthorStatus.selector, { shouldDirty: true });
                createMessage(reportThreadId!, awaitAuthorStatus, null);
            }
            else {
                console.warn("Could not set the await author status as it could not be found. The status change will be handled in the backend.")
                return;
            }
        });
    };

    const onStatusChanged = (newStatusData: DefReactSelectOption<ClientReportStatusRecipients>) => {
        const newStatus = reportStatuses.find(el => el.value === newStatusData.value);
        const originalStatus = reportStatuses.find(el => el.value === report?.statusId);
        
        const updateStatus = (statusRecord: DefReactSelectOption<ClientReportStatusRecipients>) => {
            // Handles all other status changes that are NOT the same as the current report status
            setValue('statusId', newStatusData.value, { shouldDirty: true });
            setValue('status', newStatusData.data!.selector, { shouldDirty: true });

            if (statusRecord.data!.selector !== report?.status || authorId !== report?.authorId) {
                createMessage(reportThreadId!, statusRecord.data!);
            }
            else {
                clearMessage();
            }
        }

        // reset if the same status that the report is currently on is re-selected
        if (!newStatus || newStatus.data!.selector === report?.status) {
            setValue('statusId', newStatusData.value, { shouldDirty: true });
            setValue('status', newStatusData.data!.selector, { shouldDirty: true });
            clearMessage();
            return;
        }
        else if (newStatus.data!.selector == ReportStatusSelectors.AwaitAuthor) {
            // Handles when the report is being put back to "Await Author" so it can be re-assigned to someone
            // the author is basically releasing this report back into the pool
            setValue('authorId', null, { shouldDirty: true });
            updateStatus(newStatus);
        }
        else if (newStatus.data!.selector === ReportStatusSelectors.SentToClient && isSendOptionsAvailable === true) {
            // Handles when the report is being marked as "sent to client"
            openSendOptions().then(
                () => {
                    // this will update the status of the patch report
                    updateStatus(newStatus);
                },
                () => {
                    // @ts-ignore
                    statusControlRef.current.setValue(originalStatus);
                }
            );
        }
        else {
            updateStatus(newStatus);
        }
    };

    // Boolean value that controls if the report is allowed to be "released" in that the status can go back to "await author" for it to be re-assigned to a new author
    const isAllowedToReleaseReport = React.useMemo(() => {
        // checks to see if the current status of the report will allow the author to be changed/released back into the queue
        // we do not allow this if we are compliant, terminated or at a stage where the report should be issued
        // we also do not allow if it's already sitting in the queue as `await author`
        const activeStatus = reportStatuses.find(el => el.value === statusId)?.data ?? null;
        if (!activeStatus
            || activeStatus.selector === ReportStatusSelectors.AwaitAuthor
            || activeStatus.selector === ReportStatusSelectors.IssueReport
            || activeStatus.isComplianceStatus
            || activeStatus.isTerminalStatus
        ) {
            return false;
        }
        // does a check to see if the current user is the report author currently, only they can release the report
        // we will also allow report admins (report assigners BUT NOT self-sign roles) to release the report
        if (authorId !== currentUserId) {
            return false;
        }
        return true;
    }, [ authorId, currentUserId, isAllowedToAssignAuthorQueue, reportStatuses, statusId ]);

    // Boolean value that controls if the report author select should be enabled
    const isAllowedToSetAuthor = React.useMemo(() => {
        // When the user has the role that allows them to assign anyone from the queue as the author.
        // This will allow them to basically override or change the author.
        if (isAllowedToAssignAuthorQueue) {
            return true;
        }
        // When there is no author and the current user is allowed to assign themselves to the report OR they can assign someone else
        if (!authorId && isAllowedToAssignAuthor) {
            return true;
        }
        return false;
    }, [ authorId, isAllowedToAssignAuthor, isAllowedToAssignAuthorQueue ]);

    // Boolean value that controls if the compliance user value can be set or changed.
    // Typically, this will be tied to if the report is not in a compliant or terminated status and also the current user has the correct permissions to set a compliance user.
    const isAllowedToSetComplianceUser = React.useMemo(() => {
        // Only someone with the correct role can really set the compliance user, this should be an initial check
        if (!isAllowedToAssignCompliance) {
            return false;
        }
        // Certain status values will not be able to adjust the compliance users
        // NOTE: the isReportEditable flag will also check for some of these odd statuses, the only one we need to check here is await author
        if (!isReportEditable || status === ReportStatusSelectors.AwaitAuthor) {
            return false
        }
        return true;
    }, [ isAllowedToAssignCompliance, isReportEditable, status ]);

    // Boolean value that controls if the status of a report can be set or changed.
    // Typically, this will be tied if the report has been terminated, or we are on a status such as Await Author that will typically change once the author value is set
    const isAllowedToSetReportStatus = React.useMemo(() => {
        // We will not allow the status to be changed whilst we wait for an author to be assigned.
        // The backend will adjust the status when it detects that an author-id value is assigned, which will then unlock the ability to change the status.
        if (status === ReportStatusSelectors.AwaitAuthor) {
            return false;
        }
        // You cannot change the status of the report once it has reached the terminal status, the report should now be completed.
        if (report?.isStatusTerminated === true) {
            return false;
        }
        return true;
    }, [ report, status ]);

    const isLoading = isLoadingStatuses || isProcessing;
    const isStatusLocked = isLoading || report?.isStatusTerminated === true || !isAllowedToSetReportStatus;
    const isFormLocked = isLoading || report?.isStatusCompliant === true || report?.isStatusTerminated === true;
    return (
        <div className="row gy-2">
            <div className="col-12">
                <Controller
                    name="reportName"
                    control={control}
                    rules={{ required: "Report Name is required." }}
                    render={({ field: { ref, onChange, ...rest } }) => (
                        <FormInput 
                            label="Report Name" 
                            disableAnimations={true}
                            disabled={isFormLocked} 
                            onChange={(e) => setValue('reportName', e.target.value, { shouldDirty: true })}
                            {...rest} 
                        />
                    )}
                />
            </div>
            <div className="col-12">
                <Controller
                    name="typeId"
                    control={control}
                    rules={{ required: "A Report Type must be selected." }}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <ReportTypesSelect
                            label="Report Type"
                            defaultValue={value}
                            disableAnimations={true}
                            isDisabled={!isAllowedToSetReportType || isFormLocked}
                            onChange={(values) => setValue('typeId', values.value, { shouldDirty: true })}
                            {...rest}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex flex-row align-items-end">
                <div className="flex-fill">
                    <Controller
                        name="authorId"
                        control={control}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <ReportAuthorSelect
                                label="Author"
                                defaultValue={value}
                                disableAnimations={true}
                                isDisabled={!isAllowedToSetAuthor || isFormLocked}
                                placeholder="No Author assigned."
                                onChange={onAuthorSelected}
                                {...rest}
                            />
                        )}
                    />
                </div>
                {isAllowedToReleaseReport && (
                    <div className="ms-1">
                        <Button disabled={!isAllowedToReleaseReport} onClick={onReleaseReport}>
                            <FontAwesomeIcon icon={faUserAltSlash} />
                        </Button>
                    </div>
                )}
            </div>
            <div className="col-6 col-md-3">
                <Controller
                    name="actualMinutesToComplete"
                    control={control}
                    render={({ field: { ref, onChange, ...rest } }) => (
                        <NumberInput 
                            label="Minutes to Complete" 
                            disableAnimations={true} 
                            decimalScale={0} 
                            errorMessage={formState.errors?.actualMinutesToComplete?.message}
                            errorAllowRetry={false}
                            disabled={!isAllowedToSetActualMinutes || isFormLocked}
                            onChange={(e, value) => setValue('actualMinutesToComplete', value.floatValue, { shouldDirty: true })}
                            {...rest} />
                    )}
                />
            </div>
            <div className="col-12 col-md-3">
                <Controller
                    name="deadlineDate"
                    control={control}
                    render={({ field: { ref, onBlur, onChange, ...rest } }) => (
                        <DateInput
                            label="Deadline"
                            placeholder="Choose Report deadline."
                            disableAnimations={true}
                            isClearable
                            disabled={!isAllowedToSetDeadlineDate || isFormLocked}
                            onBlur={(value, e) => {
                                if (value !== "Invalid date") {
                                    setValue('deadlineDate', value, { shouldDirty: true });
                                }
                            }}
                            {...rest}
                        />
                    )}
                />
            </div>
            <div className="col-6 d-flex flex-row align-items-end">
                <div className="flex-fill">
                    <Controller
                        name="statusId"
                        control={control}
                        render={({ field: { ref, value, onChange, ...rest } }) => (
                            <FormSelect
                                ref={statusControlRef}
                                label="Status"
                                defaultValue={value}
                                disableAnimations={true}
                                isDisabled={isStatusLocked}
                                isLoadingOptions={isLoading}
                                options={reportStatuses}
                                onChange={(rec : DefReactSelectOption<ClientReportStatusRecipients>) => onStatusChanged(rec)}
                                {...rest}
                            />
                        )}
                    />
                </div>
                {status === ReportStatusSelectors.SentToClient && (
                    <div className="ms-1">
                        <Button disabled={report?.isStatusTerminated} onClick={() => onOpenSendOptions()}>
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </div>
                )}
            </div>
            <div className="col-6">
                <Controller
                    name="complianceUserId"
                    control={control}
                    render={({ field: { ref, value, onChange, ...rest } }) => (
                        <GenericUserSelect
                            label="Compliance User"
                            defaultValue={value}
                            disableAnimations={true}
                            isDisabled={!isAllowedToAssignAuthor || !isAllowedToSetComplianceUser || isFormLocked}
                            placeholder="No Compliance user assigned."
                            onChange={(values) => setValue('complianceUserId', values.value, { shouldDirty: true })}
                            {...rest}
                        />
                    )}
                />
            </div>
        </div>
    );
};